import React from 'react';
import { Drawer, message, theme } from 'antd';
import { useNavigate } from 'react-router';
import { useProject } from '../hooks';
import { IPhase } from '../types';
import { getPhaseStatusColor, getPhaseStatusText } from '../utils/phaseFactory';

const { useToken } = theme;

interface ProjectPhaseSheetProps {
  open: boolean;
  onClose: () => void;
}

export const ProjectPhaseSheet: React.FC<ProjectPhaseSheetProps> = ({ open, onClose }) => {
  const { token } = useToken();
  const navigate = useNavigate();
  const { project, phases, activePhase, setActivePhase } = useProject();

  const visiblePhases = phases.filter(phase => !phase.isHidden);

  const handlePhaseClick = (phase: IPhase) => {
    if (phase.locked) {
      message.info('These steps will be available later');
      return;
    }

    setActivePhase(phase.id);
    if (project) {
      navigate(`/project/${project.id}/phase/${phase.id}`);
    }
    onClose();
  };

  if (!visiblePhases.length) return null;

  return (
    <Drawer
      title="Project Phases"
      placement="bottom"
      open={open}
      onClose={onClose}
      height="60vh"
      styles={{
        wrapper: { borderRadius: '12px 12px 0 0' },
        body: { padding: '12px 16px', overflowY: 'auto' },
      }}>
      <div style={{ overflowY: 'auto' }}>
        {visiblePhases.map((phase, index) => {
          const isActive = phase.id === activePhase?.id;

          return (
            <div
              key={phase.id}
              role="button"
              tabIndex={0}
              onClick={() => handlePhaseClick(phase)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handlePhaseClick(phase);
                }
              }}
              style={{
                display: 'flex',
                padding: '12px 8px',
                cursor: phase.locked ? 'default' : 'pointer',
                backgroundColor: isActive ? token.colorPrimaryBg : 'transparent',
                borderRadius: '8px',
                marginBottom: '4px',
                position: 'relative',
              }}>
              {/* Line connector */}
              {index < visiblePhases.length - 1 && (
                <div
                  style={{
                    position: 'absolute',
                    left: '20px',
                    top: '36px',
                    bottom: '-4px',
                    width: '2px',
                    backgroundColor: token.colorSplit,
                  }}
                />
              )}

              <div style={{ marginRight: '16px', zIndex: 1 }}>
                <div
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor: getPhaseStatusColor(phase, token),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}>
                  {index + 1}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 'bold',
                    color: phase.locked ? token.colorTextDisabled : token.colorText,
                  }}>
                  {phase.name}
                </div>
                <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                  {getPhaseStatusText(phase)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};
